import Box from '@mui/material/Box';
import { AttachMoney, InfoOutlined } from '@mui/icons-material';
import FormInputLabel from 'common/FormInputLabel';
import FormSelectInput from 'common/FormSelectInput';
import FormTextInput from 'common/FormTextInput';
import { serviceTimeOptions } from 'common/constant';

import { useEffect, useState } from 'react';

export default function Services({
    count,
    formData,
    handleChange,
    extraService = [],
    extraServiceDuration = [],
    extraServicePrice = [],
    setExtraServicePrice,
    extraServiceTelemedecine = [],
    extraServiceBookable = [],
    servicesOptions,
    addTelemedicine,
    onServicePriceAutoPopulate,
    onAddService,
    additionalMultipleFields,
    setAdditionalMultipleFields,
    onRemoveService,
}) {
    const [visitMode, setVisitMode] = useState(() => {
        // Initialize from existing boolean flag
        const initialTelemedicine =
            Array.isArray(extraServiceTelemedecine) && extraServiceTelemedecine.length > count
                ? extraServiceTelemedecine[count]
                : false;
        return initialTelemedicine ? 'both' : 'in_person';
    });

    // Handle auto-selection of newly added service
    useEffect(() => {
        if (additionalMultipleFields?.newServiceData && 
            additionalMultipleFields.newServiceData.index === count) {
            // Auto-select the new service
            const newServiceData = additionalMultipleFields.newServiceData;
            
            // Update the service selection
            const serviceChangeEvent = {
                target: {
                    name: 'service',
                    value: newServiceData.serviceId.toString()
                }
            };
            
            // Call the service change handler
            handleServiceChange(serviceChangeEvent);
            
            // Prepopulate the price if available from the newly created service
            if (newServiceData.servicePrice !== null && 
                newServiceData.servicePrice !== undefined && 
                onServicePriceAutoPopulate) {
                // Format the price (ensure it has 2 decimal places)
                const price = typeof newServiceData.servicePrice === 'number' 
                    ? newServiceData.servicePrice 
                    : parseFloat(newServiceData.servicePrice);
                
                if (!isNaN(price)) {
                    onServicePriceAutoPopulate(count, price);
                }
            }
            
            // Clear the new service data to prevent re-triggering
            // Note: This would need to be handled by the parent component
        }
    }, [additionalMultipleFields?.newServiceData, count]);

    // Safely get values from arrays - convert to string for FormSelectInput
    const getServiceValue = () => {
        const value = Array.isArray(extraService) && extraService.length > count ? extraService[count] : '';
        // Convert to string to match FormSelectInput expectations
        return value !== null && value !== undefined ? String(value) : '';
    };
    
    const getDurationValue = () => {
        const value = Array.isArray(extraServiceDuration) && extraServiceDuration.length > count ? extraServiceDuration[count] : '';
        // Default to 15 minutes if no value is set (for new providers)
        if (!value || value === '' || value === 0 || value === '0') {
            return '15';
        }
        return String(value);
    };
    
    // Format price with commas for display
    const formatPriceForDisplay = (price) => {
        if (!price || price === '') return '';
        // Remove commas if present, then parse
        const cleanPrice = typeof price === 'string' ? price.replace(/,/g, '') : price;
        const numPrice = parseFloat(cleanPrice);
        if (isNaN(numPrice)) return price;
        // Format with commas and 2 decimal places
        return numPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getPriceValue = () => {
        const rawValue = Array.isArray(extraServicePrice) && extraServicePrice.length > count ? extraServicePrice[count] : '';
        return formatPriceForDisplay(rawValue);
    };
    
    const getTelemedecineValue = () => {
        // First check the visit mode - if it's 'both' or 'telemedicine', telemedicine is available
        const storedVisitModes = additionalMultipleFields?.service_visit_mode || [];
        if (Array.isArray(storedVisitModes) && storedVisitModes.length > count) {
            const visitMode = storedVisitModes[count];
            if (visitMode === 'both' || visitMode === 'telemedicine') {
                return true;
            }
            if (visitMode === 'in_person' || visitMode === 'not_available') {
                return false;
            }
        }
        
        // Fallback to the boolean flag
        return Array.isArray(extraServiceTelemedecine) && extraServiceTelemedecine.length > count ? extraServiceTelemedecine[count] : false;
    };
    
    const getBookableValue = () => {
        return Array.isArray(extraServiceBookable) && extraServiceBookable.length > count ? extraServiceBookable[count] : true;
    };

    // Check if this service is incomplete (missing service name)
    const isIncomplete = () => {
        const serviceValue = getServiceValue();
        return !serviceValue || serviceValue === '' || serviceValue === 'add_new_service';
    };

    // Handle price focus - set to 0.00 and select for easy editing
    const handlePriceFocus = (e) => {
        const rawValue = Array.isArray(extraServicePrice) && extraServicePrice.length > count ? extraServicePrice[count] : '';
        if (!rawValue || rawValue === '') {
            const newPrice = [...extraServicePrice];
            newPrice[count] = '0.00';
            setExtraServicePrice(newPrice);
            setTimeout(() => {
                e.target.select();
            }, 0);
        } else {
            // Show value without commas on focus for easier editing
            const cleanValue = typeof rawValue === 'string' ? rawValue.replace(/,/g, '') : rawValue;
            e.target.value = cleanValue;
            if (cleanValue === '0.00') {
                setTimeout(() => {
                    e.target.select();
                }, 0);
            }
        }
    };

    // Handle price blur - ensure proper formatting with commas
    const handlePriceBlur = (e) => {
        const { value } = e.target;
        // Remove commas if present, then parse and format
        const cleanValue = value.replace(/,/g, '');
        const numValue = parseFloat(cleanValue);
        
        if (!isNaN(numValue) && numValue >= 0) {
            // Store without commas
            const formatted = numValue.toFixed(2);
            const newPrice = [...extraServicePrice];
            newPrice[count] = formatted;
            setExtraServicePrice(newPrice);
        } else if (cleanValue === '' || cleanValue === '0') {
            // If empty or just 0, set to 0.00
            const newPrice = [...extraServicePrice];
            newPrice[count] = '0.00';
            setExtraServicePrice(newPrice);
        }
    };

    // Handle price change with currency formatting (with commas and cents)
    const handlePriceChange = (e) => {
        const { value } = e.target;
        
        // Remove all non-numeric characters (including commas)
        const numericValue = value.replace(/[^0-9]/g, '');
        
        // Format as currency: convert to cents, then format back
        let finalValue = '';
        if (numericValue) {
            // Convert to number and divide by 100 to get dollars
            const cents = parseInt(numericValue, 10);
            const dollars = cents / 100;
            // Store without commas (for proper number handling)
            finalValue = dollars.toFixed(2);
        }
        
        const newPrice = [...extraServicePrice];
        newPrice[count] = finalValue;
        setExtraServicePrice(newPrice);
    };

    // Function to get service price by service ID
    const getServicePriceById = (serviceId) => {
        if (!servicesOptions || !serviceId) return null;
        
        // Convert serviceId to number for proper comparison
        const numericServiceId = parseInt(serviceId, 10);
        const service = servicesOptions.find(option => option.id === numericServiceId);
        
        return service ? service.price : null;
    };

    // Handle service change with price auto-population
    const handleServiceChange = (e) => {
        // Check if "Add New Service" was selected
        if (e.target.value === 'add_new_service') {
            if (onAddService) {
                onAddService(count); // Pass the current service index
            }
            return;
        }
        
        // Auto-populate price if a service is selected and onServicePriceAutoPopulate is provided
        if (e.target.name === 'service' && e.target.value && onServicePriceAutoPopulate) {
            const servicePrice = getServicePriceById(e.target.value);
            if (servicePrice !== null && servicePrice !== undefined) {
                // Call auto-populate first, then the original handler
                onServicePriceAutoPopulate(count, servicePrice);
            } else {
            }
        }
        
        // Call the original handleChange after auto-population
        handleChange(e);
    };

    // Get dropdown options - always show all options regardless of consultation types
    // Individual services can be configured independently of provider-level consultations
    const getVisitModeOptions = () => {
        return [
            { id: 'both', service: 'In-Person & Telemedicine' },
            { id: 'in_person', service: 'In-Person only' },
            { id: 'telemedicine', service: 'Telemedicine only' },
            { id: 'not_available', service: 'Not offered' },
        ];
    };

    // Get stored visit mode value or derive from flags
    const getVisitModeValue = () => {
        // First, try to get the stored visit mode value from additionalMultipleFields
        const storedVisitModes = additionalMultipleFields?.service_visit_mode || [];
        if (Array.isArray(storedVisitModes) && storedVisitModes.length > count && storedVisitModes[count]) {
            return storedVisitModes[count];
        }
        
        // Fallback: derive from boolean flags if no stored value
        const isBookable = getBookableValue();
        const isTelemedicine = getTelemedecineValue();
        
        // If not bookable, return 'not_available'
        if (!isBookable) {
            return 'not_available';
        }
        
        // If bookable, determine based on telemedicine flag
        // Default to 'both' when telemedicine is true, 'in_person' when false
        return isTelemedicine ? 'both' : 'in_person';
    };

    // Handle change for visit mode dropdown and map to existing boolean fields
    const handleVisitModeChange = (e) => {
        const { value } = e.target; // 'both' | 'in_person' | 'telemedicine' | 'not_available'
        
        // Store the visit mode value so we can retrieve it later
        const storedVisitModes = additionalMultipleFields?.service_visit_mode || [];
        const updatedVisitModes = Array.isArray(storedVisitModes) ? [...storedVisitModes] : [];
        updatedVisitModes[count] = value;
        
        // Update additionalMultipleFields with the visit mode value
        if (setAdditionalMultipleFields) {
            setAdditionalMultipleFields((prev) => ({
                ...prev,
                service_visit_mode: updatedVisitModes,
            }));
        }
        
        // Update bookable flag
        const updatedBookable = Array.isArray(extraServiceBookable) ? [...extraServiceBookable] : [];
        updatedBookable[count] = value !== 'not_available';
        
        // Update telemedicine flag
        const updatedTelemedicine = Array.isArray(extraServiceTelemedecine) ? [...extraServiceTelemedecine] : [];
        // Map to boolean: in_person => false, telemedicine or both => true
        updatedTelemedicine[count] = value === 'in_person' ? false : (value === 'not_available' ? false : true);
        
        // Update both fields
        handleChange({
            target: {
                name: 'service_bookable',
                value: updatedBookable[count],
                checked: updatedBookable[count]
            }
        });
        
        handleChange({
            target: {
                name: 'service_telemedecine',
                value: updatedTelemedicine[count],
                checked: updatedTelemedicine[count]
            }
        });
    };

    // Keep service-level availability independent from provider-level consultation toggles.
    // This prevents user-selected visit modes (e.g. telemedicine-only) from being overwritten.

    return (
        <Box 
            id={`service-${count}`}
            style={{ 
                border: isIncomplete() ? '1px solid #D1D5DB' : '1px solid #E1E1E1', 
                borderRadius: '8px', 
                padding: '16px',
                paddingLeft: '16px',
                paddingRight: '16px',
                marginBottom: '16px',
                marginLeft: '18px',
                backgroundColor: 'white',
                position: 'relative'
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <FormInputLabel label={`Service ${count + 1}`} labelSize={12} labelBottomMargin={'-mb-1'} style={{ marginBottom: '0' }} />
                {isIncomplete() && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '2px 8px',
                            backgroundColor: '#F3F4F6',
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: '#6B7280',
                            fontFamily: 'Poppins, sans-serif',
                        }}
                        title="This service is incomplete. Please select a service name to save it."
                    >
                        <InfoOutlined sx={{ fontSize: '14px', color: '#6B7280' }} />
                        <span>Incomplete</span>
                    </Box>
                )}
            </div>
            {servicesOptions && (
                <>
                    <FormSelectInput
                        label="Service"
                        name="service"
                        value={getServiceValue()}
                        handleChange={handleServiceChange}
                        required={false}
                        inputSize={12}
                        placeholder={'Select/add a service'}
                        options={servicesOptions}
                        optionKey="id"
                        optionValue={['service']}
                        noLabel={true}
                    />

                    <div className={'flex items-center justify-between gap-4 mt-4'}>
                        <FormSelectInput
                            name="service_duration"
                            value={getDurationValue()}
                            handleChange={handleChange}
                            required={false}
                            inputSize={8}
                            placeholder={'Select a time duration'}
                            options={serviceTimeOptions}
                            optionKey="id"
                            optionValue={['service']}
                            noLabel={true}
                            style={{ marginBottom: '16px' }}
                        />

                        <FormTextInput
                            value={getPriceValue()}
                            label="Service Price"
                            name="service_price"
                            handleChange={handlePriceChange}
                            placeholder="0.00"
                            subText=""
                            required={false}
                            index={1}
                            type="text"
                            style={{ marginBottom: '16px' }}
                            inputProps={{
                                style: { textAlign: 'right' },
                                onFocus: handlePriceFocus,
                                onBlur: handlePriceBlur,
                                onKeyDown: (ev) => {
                                    const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                                    if (allowed.includes(ev.key)) return;
                                    const isDigit = /[0-9]/.test(ev.key);
                                    if (!isDigit) {
                                        ev.preventDefault();
                                        return;
                                    }
                                },
                                onPaste: (ev) => {
                                    const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                    // Allow pasting numbers with or without commas/decimals
                                    if (!/^[\d,.]*$/.test(paste)) {
                                        ev.preventDefault();
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box
                                        sx={{
                                            backgroundColor: 'rgba(203, 193, 183, 1)',
                                            color: 'rgba(87, 83, 78, 1)',
                                            height: '42px',
                                            width: '42px !important',
                                            minWidth: '42px !important',
                                            maxWidth: '42px !important',
                                            flexBasis: '42px !important',
                                            flexGrow: '0 !important',
                                            flexShrink: '0 !important',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: '-14px',
                                            borderTopLeftRadius: '7px',
                                            borderBottomLeftRadius: '7px',
                                        }}
                                    >
                                        <AttachMoney fontSize="small" />
                                    </Box>
                                ),
                                sx: {
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                        textAlign: 'right',
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& input[type=number]:hover': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& input[type=number]:focus': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& input[type=number]:focus-visible': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& input[type=number]:focus-within': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& fieldset': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& fieldset:hover': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& fieldset:focus': {
                                        borderTopLeftRadius: '0px',
                                        borderBottomLeftRadius: '0px',
                                    },
                                    '& .MuiInputAdornment-root': {
                                        maxWidth: 'none !important',
                                        minWidth: '42px !important',
                                        width: '42px !important',
                                        flexBasis: '42px !important',
                                        flexGrow: '0 !important',
                                        flexShrink: '0 !important',
                                    },
                                    '& input[type=number]::-webkit-outer-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                }
                            }}
                            noLabel={true}
                            textSize={4}
                        />
                    </div>
                    <div className={'flex items-center gap-2'}>
                        <FormSelectInput
                            name="service_visit_mode"
                            handleChange={handleVisitModeChange}
                            value={getVisitModeValue()}
                            required={false}
                            inputSize={12}
                            placeholder={'Select service availability'}
                            options={getVisitModeOptions()}
                            optionKey="id"
                            optionValue={['service']}
                            className="long-label"
                            noLabel={true}
                            style={{ width: '100%', marginBottom: '16px' }}
                        />
                    </div>
                    {onRemoveService && (
                        <Box
                            className={'danger-link cursor-pointer'}
                            onClick={onRemoveService}
                            style={{ whiteSpace: 'nowrap', flexShrink: 0, marginTop: '16px' }}
                        >
                            - Remove Service
                        </Box>
                    )}
                    </>
                )}
            </Box>
    );
}
